import type { ItemFieldRow } from './item-field-reader';
import type { AbilityConditionDependencies } from './ability-conditions';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { createFormulaReadContext } from './formula-read-context';
import { createInventoryReadContext } from './inventory-read-context';
import { containsAbilityTriggerKey } from './inventory-ability-grant-helpers';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { resolveLayerEntry } from './layer-entry-resolver';
import { appendCompiledLayerInPlace } from './status-row-construction';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpJsonInteger } from './php-integer-arithmetic';
import { applyPermanentStatusDefenseDelta, STATUS_DAMAGE_DEFENSE_LAYERS } from './creature-defense-delta';
import type { PermanentStatusDefenseChange } from './creature-defense-delta';
import { readCreatureStatuses } from './creature-status-reader';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, k: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, k) ? (v as Row)[k] : undefined;
const valid = (v: string): boolean => /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(v);
const upper = (v: string): string => v.replace(/[a-z]/g, c => c.toUpperCase());
const lower = (v: string): string => v.replace(/[A-Z]/g, c => c.toLowerCase());
function copy<T>(v: T): T {
    if (!arrayLike(v)) return v;
    const entries = Object.entries(v);
    return (entries.every(([key], i) => key === String(i)) ? entries.map(([, child]) => copy(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, copy(child)]))) as T;
}
function strictEqual(a: unknown, b: unknown): boolean {
    if (!arrayLike(a) || !arrayLike(b)) return a === b;
    const left = Object.entries(a), right = Object.entries(b);
    return left.length === right.length && left.every(([key, value], i) => key === right[i]![0] && strictEqual(value, right[i]![1]));
}
/** Actual caster_class_level_map: twenty PC slots, later duplicate IDs win,
 * subclasses share their parent slot's level. Keep PHP integers exact internally.
 */
function classLevels(host: ItemFieldRow): Map<string, bigint> {
    const pc = own(host, 'player_characters'), map = new Map<string, bigint>();
    if (!arrayLike(pc)) return map;
    for (let slot = 1; slot <= 20; slot++) {
        if (!phpJsonTruthy(own(pc, `class_${slot}`))) continue;
        const level = phpJsonInteger(own(pc, `class_level_${slot}`));
        for (const key of [`class_${slot}`, `subclass_${slot}`]) {
            const id = phpJsonString(own(pc, key));
            if (id !== '' && valid(id)) map.set(lower(id), level);
        }
    }
    return map;
}
export interface PassivePermanentStatusDependencies extends AbilityConditionDependencies {
    queryFullItem: (id: string) => ItemFieldRow | null;
}
/** Native passive materialization followed by strict-value immunity removal.
 * Only resolved definitions enter the PC level gate; the native fallback query
 * branch of meet_ability_requirements is therefore not part of this caller.
 */
export function createPassivePermanentStatusCompiler(deps: PassivePermanentStatusDependencies) {
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const common = {...deps, inventoryContext}, readers = createFormulaReadContext(common), conditions = createAbilityConditionEvaluator(common);
    const query = (id: string): ItemFieldRow | null => { emit(['full-item', id]); return deps.queryFullItem(id); };
    function visitActive(host: ItemFieldRow, grant: (id: string, passiveId: string) => void): void {
        const passives = readers.layerEntries(host, 'passive_ability');
        const levels = classLevels(host);
        for (const entry of Object.values(passives)) {
            const nested = own(entry, 'item_arr');
            const ability = arrayLike(nested) ? nested : resolveLayerEntry(entry, host, query);
            if (!arrayLike(ability) || Object.keys(ability).length === 0) continue;
            const applied = own(entry, 'built_from_item_type') === 'status_effect';
            if (containsAbilityTriggerKey(ability) && !applied) continue;
            const sourceId = phpJsonString(own(entry, 'source'));
            const statusDelivered = applied || (sourceId !== '' && Object.values(readCreatureStatuses(host, 'target')).some(status =>
                arrayLike(status) && lower(phpJsonString(readers.layerValue(status, 'status_id'))) === lower(sourceId)));
            // The native caller always supplies item_arr as the resolved definition.
            if (arrayLike(own(host, 'player_characters')) && !statusDelivered) {
                const total = phpJsonInteger(readers.layerValue(host, 'total_level'));
                const required = readers.layerValue(ability, 'requires_level');
                if (required !== null && required !== '' && phpJsonInteger(required) > total) continue;
                const requiredClass = readers.layerValue(ability, 'requires_class_level');
                if (requiredClass !== null && requiredClass !== '') {
                    const source = lower(phpJsonString(own(entry, 'source')));
                    const level = levels.get(source) ?? total;
                    if (phpJsonInteger(requiredClass) > level) continue;
                }
            }
            if (!conditions.check(ability, host, false, false)) continue;
            for (const status of Object.values(readers.layerEntries(ability, 'status_effect_permanent'))) {
                const id = readLayerListEntryUuid(status);
                if (id === '' || !valid(id)) continue;
                grant(id, readLayerListEntryUuid(entry) || phpJsonString(readers.itemField(ability, 'item_id')));
            }
        }
    }
    function apply(input: ItemFieldRow): ItemFieldRow {
        if (!arrayLike(input)) throw new TypeError('Permanent passive materialization requires a PHP array');
        const host = Object.fromEntries(Object.entries(copy(input)));
        const seen = new Set(Object.values(readers.layerEntries(host, 'status_effect_permanent')).map(entry => upper(readLayerListEntryUuid(entry))));
        visitActive(host, id => {
            if (seen.has(upper(id))) return;
            appendCompiledLayerInPlace(host, 'status_effect_permanent', id);
            seen.add(upper(id));
        });
        return copy(host);
    }
    /** Called after the mutation's ordinary conditions/grants have settled.
     * The bookkeeping distinguishes passive-owned statuses from authored or
     * independently granted statuses. It travels with the held computed row.
     * Only the status list, its ownership map and affected defense stores change.
     */
    function reconcile(host: Row, foldDefenses = true): { added: string[]; removed: string[] } {
        const computed = (host.computed ??= {}) as Row, creature = (computed.creature ??= {}) as Row;
        const abilities = (creature.abilities ??= {}) as Row;
        const prior = arrayLike(creature.passive_permanent_status_sources) ? creature.passive_permanent_status_sources as Row : {};
        const original = Object.values(readers.layerEntries(host, 'status_effect_permanent'));
        const slot = abilities.status_effect_permanent;
        const originalIds = new Set(original.map(entry => upper(readLayerListEntryUuid(entry))));
        const next = original.filter(entry => typeof entry !== 'string' || own(prior, upper(readLayerListEntryUuid(entry))) !== true);
        const authored = new Set(next.map(entry => upper(readLayerListEntryUuid(entry))));
        const sources: Row = {}, seen = new Set(authored);
        const entering: PermanentStatusDefenseChange[] = [];
        // Temporarily expose the evolving final status context to the shared
        // condition reader. On a no-op the held slot is restored by identity.
        abilities.status_effect_permanent = next;
        try {
            visitActive(host, id => {
                const key = upper(id);
                if (!authored.has(key)) sources[key] = true;
                if (seen.has(key)) return;
                if (foldDefenses && !originalIds.has(key)) {
                    const status = resolveLayerEntry(id, host, query);
                    if (!arrayLike(status)) throw new Error(`def-missing:${id}`);
                    entering.push({ id, name: phpJsonString(readers.itemField(status, 'item_name') ?? 'Permanent Status Effect'),
                        fragments: Object.fromEntries([...STATUS_DAMAGE_DEFENSE_LAYERS, 'immune_status_effect'].map(layer => [layer,
                            Object.values(readers.layerEntries(status, layer))])) });
                }
                next.push(id); seen.add(key);
            });
            const immune = Object.values(readers.layerEntries(host, 'immune_status_effect'));
            for (let i = next.length - 1; i >= 0; i--) if (immune.some(value => strictEqual(next[i], value))) {
                delete sources[upper(readLayerListEntryUuid(next[i]))];
                next.splice(i, 1);
            }
        } catch (error) {
            if (slot === undefined) delete abilities.status_effect_permanent; else abilities.status_effect_permanent = slot;
            throw error;
        }
        const same = next.length === original.length && next.every((entry, i) => entry === original[i]);
        if (same) {
            if (slot === undefined) delete abilities.status_effect_permanent;
            else abilities.status_effect_permanent = slot;
        }
        const ids = next.map(readLayerListEntryUuid), nextIds = new Set(ids.map(upper));
        const added = ids.filter(id => !originalIds.has(upper(id)));
        const removed = original.map(readLayerListEntryUuid).filter(id => !nextIds.has(upper(id)));
        if (Object.keys(sources).length) {
            if (Object.keys(sources).length !== Object.keys(prior).length || Object.keys(sources).some(id => prior[id] !== true)) creature.passive_permanent_status_sources = sources;
        } else delete creature.passive_permanent_status_sources;
        if (foldDefenses && (added.length || removed.length)) {
            const defenses = applyPermanentStatusDefenseDelta({ defenses: arrayLike(creature.defenses) ? creature.defenses as Row : undefined,
                hostId: phpJsonString(readers.itemField(host, 'item_id')), statusIds: ids,
                entering: entering.filter(status => nextIds.has(upper(status.id))), leaving: removed });
            if (defenses) creature.defenses = defenses; else delete creature.defenses;
        }
        return { added, removed };
    }
    function removeImmune(input: ItemFieldRow): ItemFieldRow {
        if (!arrayLike(input)) throw new TypeError('Permanent immunity removal requires a PHP array');
        const host = Object.fromEntries(Object.entries(copy(input)));
        const immune = Object.values(readers.layerEntries(host, 'immune_status_effect'));
        const permanent = Object.values(readers.layerEntries(host, 'status_effect_permanent'));
        if (immune.length && permanent.length) host.status_effect_permanent = permanent.filter(entry => !immune.some(value => strictEqual(entry, value)));
        return copy(host);
    }
    return {apply, removeImmune, reconcile,
        // Initial construction may copy; ordinary gain/loss calls reconcile on
        // the held row after the mutation, never this immutable entry point.
        initialize: (input: ItemFieldRow, foldDefenses = false): ItemFieldRow => {
            const host = Object.fromEntries(Object.entries(copy(input)));
            const permanent = Object.values(readers.layerEntries(host, 'status_effect_permanent'));
            const computed = (host.computed ??= {}) as Row, creature = (computed.creature ??= {}) as Row;
            const abilities = (creature.abilities ??= {}) as Row;
            if (permanent.length) abilities.status_effect_permanent = permanent;
            reconcile(host, foldDefenses);
            // Relocation belongs only to initial construction's private copy.
            delete host.status_effect_permanent;
            if (arrayLike(host.item_layer_types)) delete (host.item_layer_types as Row).status_effect_permanent;
            return host;
        },
        all: (input: ItemFieldRow): ItemFieldRow => removeImmune(apply(input))};
}
